/**
 * Ajouts AGORA (absents du bundle de référence) – fenêtres de dialogue modales, sur Radix UI :
 *   Dialog        : fenêtre modale (formulaire court, détail, choix) – Radix Dialog ;
 *   ConfirmDialog : demande de confirmation d'une action (fermer avec un brouillon, supprimer…) – Radix AlertDialog.
 * Comportement fourni par Radix : focus placé dans la fenêtre et gardé dedans, Échap pour fermer, focus rendu à
 * l'élément d'origine, page derrière inerte pour les lecteurs d'écran et sans défilement, titre et description reliés.
 * Apparence : verre dense du design (`ag-glass ag-glass-strong`) sur le voile de la palette de commandes (`agx-voile`),
 * styles `agx-dialogue…` dans core/app.css. Tous les textes sont fournis traduits par l'appelant.
 */
import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Button, cx, h, Icon } from './base';

/** Attribut posé sur le contenu des fenêtres modales (les panneaux non modaux, ex. dock, restent ouverts dessous). */
export const ATTRIBUT_MODALE = 'data-agx-modale';
/** Props du marqueur (étalées : attribut data- hors du type des props Radix). */
const MARQUE_MODALE: Record<string, string> = { [ATTRIBUT_MODALE]: '' };

/**
 * Fenêtre ouverte par du code (sans bouton déclencheur) : Radix rend le focus au déclencheur, absent ; on le rend à
 * l'élément qui l'avait à l'ouverture (ex. le « × » qui a demandé la confirmation). À étaler sur le Content Radix.
 */
export function useRetourFocus(actif = true): { onOpenAutoFocus?: (e: Event) => void; onCloseAutoFocus?: (e: Event) => void } {
  const origine = React.useRef<HTMLElement | null>(null);
  if (!actif) return {};
  return {
    // Appelé avant que Radix déplace le focus : l'élément actif est encore celui d'origine.
    onOpenAutoFocus: () => { origine.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; },
    onCloseAutoFocus: (e: Event) => {
      if (origine.current?.isConnected && origine.current !== document.body) { e.preventDefault(); origine.current.focus(); }
    },
  };
}

export interface DialogProps {
  title: string; description?: string; children?: React.ReactNode;
  /** Boutons d'action, alignés à droite (le bouton principal en dernier). */
  footer?: React.ReactNode;
  /** Libellé du bouton × (traduit). */
  closeLabel: string;
  /** État piloté ; sans `open`, la fenêtre s'ouvre par `trigger` et gère son état seule. */
  open?: boolean; onOpenChange?: (open: boolean) => void; defaultOpen?: boolean;
  /** Bouton qui ouvre la fenêtre : un élément qui transmet sa référence et ses props (Button…). */
  trigger?: React.ReactElement;
  /** Largeur : sm 420 px, md 560 px (défaut), lg 760 px – toujours bornée par l'écran. */
  size?: 'sm' | 'md' | 'lg';
}

export function Dialog(p: DialogProps) {
  const retourFocus = useRetourFocus(!p.trigger);
  return h(RadixDialog.Root, { open: p.open, onOpenChange: p.onOpenChange, defaultOpen: p.defaultOpen },
    p.trigger ? h(RadixDialog.Trigger, { asChild: true }, p.trigger) : null,
    h(RadixDialog.Portal, null,
      h(RadixDialog.Overlay, { className: cx('agx-voile', 'agx-voile-dialogue') },
        h(RadixDialog.Content, {
          className: cx('agx-dialogue', 'agx-dialogue-' + (p.size || 'md'), 'ag-glass', 'ag-glass-strong'), ...MARQUE_MODALE, ...retourFocus,
          // Sans description, rien à relier (évite l'avertissement de Radix).
          ...(p.description ? {} : { 'aria-describedby': undefined }),
        },
        h('header', { className: 'agx-dialogue-tete' },
          h(RadixDialog.Title, { className: 'agx-dialogue-titre' }, p.title),
          h(RadixDialog.Close, { asChild: true },
            h('button', { type: 'button', className: 'ag-page-x', 'aria-label': p.closeLabel, title: p.closeLabel }, h(Icon, { name: 'close', size: 14 })))),
        p.description ? h(RadixDialog.Description, { className: 'agx-dialogue-texte' }, p.description) : null,
        p.children ? h('div', { className: 'agx-dialogue-corps' }, p.children) : null,
        p.footer ? h('footer', { className: 'agx-dialogue-pied' }, p.footer) : null))));
}

export interface ConfirmDialogProps {
  title: string; description?: string; confirmLabel: string; cancelLabel: string;
  /** Action destructive ou irréversible : bouton de confirmation « danger ». */
  danger?: boolean;
  onConfirm: () => void; onCancel?: () => void;
  open?: boolean; onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactElement;
}

/**
 * Confirmation : pas de fermeture par un clic à côté (il faut choisir), Échap = Annuler, focus initial sur Annuler
 * (choix le moins risqué). Pour demander une confirmation depuis du code, préférer `useConfirmation()` du socle.
 */
export function ConfirmDialog(p: ConfirmDialogProps) {
  const retourFocus = useRetourFocus(!p.trigger);
  return h(AlertDialog.Root, { open: p.open, onOpenChange: p.onOpenChange },
    p.trigger ? h(AlertDialog.Trigger, { asChild: true }, p.trigger) : null,
    h(AlertDialog.Portal, null,
      h(AlertDialog.Overlay, { className: cx('agx-voile', 'agx-voile-dialogue') },
        h(AlertDialog.Content, {
          className: cx('agx-dialogue', 'agx-dialogue-sm', 'ag-glass', 'ag-glass-strong'), ...MARQUE_MODALE, ...retourFocus,
          ...(p.description ? {} : { 'aria-describedby': undefined }),
        },
        h('header', { className: 'agx-dialogue-tete' }, h(AlertDialog.Title, { className: 'agx-dialogue-titre' }, p.title)),
        p.description ? h(AlertDialog.Description, { className: 'agx-dialogue-texte' }, p.description) : null,
        h('footer', { className: 'agx-dialogue-pied' },
          h(AlertDialog.Cancel, { asChild: true }, h(Button, { variant: 'quiet', onClick: p.onCancel }, p.cancelLabel)),
          h(AlertDialog.Action, { asChild: true }, h(Button, { variant: p.danger ? 'danger' : 'primary', onClick: p.onConfirm }, p.confirmLabel)))))));
}
